/** Pure derivations for the Shared-with-me page (D32): every model shared
 *  with the caller from another org, plus direct shares from their own. */

interface SharedModelLike {
  space_id?: string | null;
  via?: string;
  relation?: string;
  scheduled_deletion_date?: number;
}

/** `direct` alone also covers a legacy direct `owner` tuple, hence the
 *  relation check. */
export const selectSharedModels = <T extends SharedModelLike>(
  models: T[],
): T[] =>
  models.filter(
    (m) =>
      !m.scheduled_deletion_date &&
      (m.via === "external" || (m.via === "direct" && m.relation !== "Owner")),
  );

export interface SharedPartition<T> {
  /** Rows nested under a granted space, keyed by space_id. A granted space
   *  with no visible rows still renders: its models may not have loaded yet. */
  bySpace: Map<string, T[]>;
  /** Rows no granted space claims: direct model shares. */
  standalone: T[];
}

export const partitionSharedModels = <T extends SharedModelLike>(
  sharedModels: T[],
  grantedSpaceIds: ReadonlySet<string>,
): SharedPartition<T> => {
  const bySpace = new Map<string, T[]>();
  const standalone: T[] = [];
  for (const model of sharedModels) {
    const spaceId = model.space_id ?? undefined;
    if (spaceId && grantedSpaceIds.has(spaceId)) {
      const bucket = bySpace.get(spaceId) ?? [];
      bucket.push(model);
      bySpace.set(spaceId, bucket);
    } else {
      standalone.push(model);
    }
  }
  return { bySpace, standalone };
};

/** "viewer" → "Viewer" — the wire carries FGA relation spellings. */
export const roleLabel = (role: string): string =>
  role.length > 0 ? role.charAt(0).toUpperCase() + role.slice(1) : role;
